import React, { useRef, useState } from "react";
import { Box, Stack, TextField, Avatar, Menu, MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import {
  COLORS,
  RADIUS,
  PRIORITIES,
  PRIORITY_MAP,
  TASK_STATUSES,
  TASK_STATUS_MAP,
  formatDateInput,
  formatDateDisplay,
  initialsOf,
} from "./ticketTheme";

/** One row of the Projects table: title (click-to-edit), status pill,
 * priority pill, due date, assignee, delete. `orgUsers` is the already-
 * flattened [{id, name}] roster (see ProjectsBoard). */
export default function TaskRow({
  task,
  orgUsers,
  onUpdate,
  onMove,
  onAssign,
  onDelete,
}) {
  const [title, setTitle] = useState(task.title);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [priorityAnchor, setPriorityAnchor] = useState(null);
  const [assigneeAnchor, setAssigneeAnchor] = useState(null);
  const dateInputRef = useRef(null);

  const taskStatus = TASK_STATUS_MAP[task.status] || TASK_STATUSES[0];
  const priority = PRIORITY_MAP[task.priority] || PRIORITY_MAP.medium;

  const commitTitle = () => {
    const trimmed = title.trim();
    if (trimmed && trimmed !== task.title) onUpdate({ title: trimmed });
    else setTitle(task.title);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        py: 1,
        borderBottom: "1px solid",
        borderColor: COLORS.border,
        "&:hover .task-row-delete": { opacity: 1 },
      }}
    >
      <Box sx={{ flex: "1 1 320px", minWidth: 200 }}>
        <TextField
          variant="standard"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          InputProps={{ disableUnderline: true }}
          fullWidth
          sx={{
            "& input": {
              fontSize: 14,
              fontWeight: 600,
              color: COLORS.textPrimary,
            },
          }}
        />
      </Box>

      <Box sx={{ width: 140 }}>
        <Box
          onClick={(e) => setStatusAnchor(e.currentTarget)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            bgcolor: taskStatus.soft,
            color: taskStatus.color,
            borderRadius: `${RADIUS.pill}px`,
            px: 1.25,
            py: 0.4,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: taskStatus.color,
            }}
          />
          {taskStatus.label}
        </Box>
        <Menu
          anchorEl={statusAnchor}
          open={Boolean(statusAnchor)}
          onClose={() => setStatusAnchor(null)}
        >
          {TASK_STATUSES.map((s) => (
            <MenuItem
              key={s.value}
              onClick={() => {
                onMove(s.value);
                setStatusAnchor(null);
              }}
            >
              {s.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box sx={{ width: 120 }}>
        <Box
          onClick={(e) => setPriorityAnchor(e.currentTarget)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            bgcolor: priority.soft,
            color: priority.color,
            borderRadius: `${RADIUS.pill}px`,
            px: 1.25,
            py: 0.4,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {priority.label}
        </Box>
        <Menu
          anchorEl={priorityAnchor}
          open={Boolean(priorityAnchor)}
          onClose={() => setPriorityAnchor(null)}
        >
          {PRIORITIES.map((p) => (
            <MenuItem
              key={p.value}
              onClick={() => {
                onUpdate({ priority: p.value });
                setPriorityAnchor(null);
              }}
            >
              {p.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
          width: 160,
          color: COLORS.textSecondary,
          fontSize: 13,
          position: "relative",
        }}
      >
        <span>{formatDateDisplay(task.due_date) || "dd-mm-yyyy"}</span>
        <Box
          component="button"
          type="button"
          onClick={() =>
            dateInputRef.current?.showPicker?.() ||
            dateInputRef.current?.click()
          }
          sx={{
            border: "none",
            bgcolor: "transparent",
            cursor: "pointer",
            display: "inline-flex",
            p: 0.25,
          }}
        >
          <CalendarTodayIcon
            sx={{ fontSize: 14, color: COLORS.textDisabled }}
          />
        </Box>
        <input
          ref={dateInputRef}
          type="date"
          value={formatDateInput(task.due_date)}
          onChange={(e) => onUpdate({ due_date: e.target.value || null })}
          style={{
            position: "absolute",
            opacity: 0,
            width: 0,
            height: 0,
            pointerEvents: "none",
          }}
        />
      </Stack>

      <Box sx={{ width: 130 }}>
        {task.assignee_id ? (
          <Box
            onClick={(e) => setAssigneeAnchor(e.currentTarget)}
            sx={{ cursor: "pointer", display: "inline-flex" }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: 11,
                bgcolor: COLORS.accent,
              }}
            >
              {initialsOf(task.assignee_name)}
            </Avatar>
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={(e) => setAssigneeAnchor(e.currentTarget)}
            sx={{
              border: "none",
              bgcolor: "transparent",
              color: COLORS.textDisabled,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            + Assign
          </Box>
        )}
        <Menu
          anchorEl={assigneeAnchor}
          open={Boolean(assigneeAnchor)}
          onClose={() => setAssigneeAnchor(null)}
        >
          {(orgUsers || []).length === 0 && (
            <MenuItem disabled>No org members found</MenuItem>
          )}
          {(orgUsers || []).map((u) => (
            <MenuItem
              key={u.id}
              onClick={() => {
                onAssign(u.id);
                setAssigneeAnchor(null);
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
                  {initialsOf(u.name)}
                </Avatar>
                <span>{u.name}</span>
              </Stack>
            </MenuItem>
          ))}
          {task.assignee_id && (
            <MenuItem
              onClick={() => {
                onAssign(null);
                setAssigneeAnchor(null);
              }}
            >
              Unassign
            </MenuItem>
          )}
        </Menu>
      </Box>

      <Box
        className="task-row-delete"
        onClick={onDelete}
        sx={{
          opacity: 0,
          transition: "opacity 120ms ease",
          cursor: "pointer",
          color: COLORS.textDisabled,
          "&:hover": { color: COLORS.danger },
          px: 1,
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </Box>
    </Stack>
  );
}
